import { FunctionComponent, useState, useCallback, useMemo } from "react";
import { useId } from "react-id-generator";
import { debounce } from "lodash";
import { useInViewEffect } from "react-hook-inview";
import {
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  MenuItem,
  ListSubheader,
  TextField,
} from "@mui/material";
import { ArrowDropDown, Close } from "@mui/icons-material";
import { useDictionary } from "hooks/use-dictionary";

interface FieldSelectProps {
  label: string;
  options: { value: any; label: string }[];
  value: any;
  onChange: (value: any) => void;
  setFilters?: (filters: any) => void;
  onBottomReached?: () => void;
  lazyLoadable?: boolean;
}

const FieldSelect: FunctionComponent<FieldSelectProps> = ({
  label,
  options,
  value,
  onChange,
  setFilters,
  onBottomReached = () => {},
  lazyLoadable = false,
}) => {
  const [labelId] = useId(1);
  const ids = useId(options.length);
  const dict = useDictionary();
  const [search, setSearch] = useState<string>("");
  const [open, setOpen] = useState(false);

  const ref = useInViewEffect(
    ([entry], observer) => {
      if (entry.isIntersecting && lazyLoadable) {
        debouncedLoadMoreData();
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0.5 }
  );

  const debouncedLoadMoreData = useMemo(() => {
    if (lazyLoadable && onBottomReached) {
      return debounce(onBottomReached, 500);
    }
    return () => {};
  }, [onBottomReached, lazyLoadable]);

  const debouncedSetFilters = useMemo(
    () =>
      debounce((newValue: string) => {
        if (setFilters) {
          setFilters({ q: newValue });
        }
      }, 500),
    [setFilters]
  );

  const handleSearchChange = useCallback(
    (newValue: string) => {
      setSearch(newValue);
      debouncedSetFilters(newValue);
    },
    [setSearch, debouncedSetFilters]
  );

  return (
    <FormControl fullWidth>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        labelId={labelId}
        input={
          <OutlinedInput
            label={label}
            endAdornment={
              <Close
                className="cursor-pointer text-theme-gray300"
                onClick={() => onChange("")}
              />
            }
          />
        }
        IconComponent={() =>
          value && value.length > 0 ? null : (
            <ArrowDropDown className="mr-[7px] text-theme-gray300" />
          )
        }
        MenuProps={{
          autoFocus: false,
          disableAutoFocusItem: true,
        }}
      >
        {lazyLoadable && (
          <ListSubheader
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                setOpen(false);
                return;
              }

              event.stopPropagation();
            }}
          >
            <TextField
              placeholder={dict.common.search}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
              margin="dense"
            />
          </ListSubheader>
        )}
        {options.map((option, i) => (
          <MenuItem key={ids[i]} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
        <div key={options.length} ref={ref} style={{ height: "1px" }}></div>
      </Select>
    </FormControl>
  );
};

export default FieldSelect;
